import { Address, AddressDraft } from "@modules/address/addressTypes"
import { EmptyAddress } from "@modules/address/addressUtils"
import { BoxesStaffMember } from "@modules/auth/userProfileDto"
import { PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { HUImageDraft } from "@modules/dtos/imageDto"
import { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { HUImage } from "@modules/types/imageTypes"

export enum GenderCategoryEnum {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export type HidableAttributeGenderCategory = {
  value?: GenderCategoryEnum
  visibility: {
    all: boolean
  }
}

export type HidableAttributeString = {
  value: string | undefined
  visibility: {
    all: boolean
  }
}

export type HidableAttributeFloat = {
  value: number | undefined
  visibility: {
    all: boolean
  }
}

export type UserProfile = {
  id: string
  email: string
  firstname: string
  lastname: string
  username: string
  avatar?: HUImage
  locale: string // todo
  inscriptionDate: Date
  notificationsEnabled: boolean
  stripeAccountStatus?: StripeAccountStatus
  stripeAccountCurrency?: string
  stripeMinimumAmount?: number
  stripeMaximumAmount?: number
  stripeAccountId?: string
  biography?: string
  emergencyInformation?: string
  birthdate?: HidableAttributeString
  crossfitStartDate?: HidableAttributeString
  nationality?: HidableAttributeString
  job?: HidableAttributeString
  genderCategory?: HidableAttributeGenderCategory
  weight?: HidableAttributeFloat
  height?: HidableAttributeFloat
  phoneNumber?: PhoneNumberWithPrefix
  address?: Address
  boxesStaffMember: BoxesStaffMember[]
}

export type UserProfileDraft = {
  firstname: string
  lastname: string
  username: string
  biography?: string
  avatar?: HUImageDraft
  genderCategory?: HidableAttributeGenderCategory
  birthdate?: HidableAttributeString
  nationality?: HidableAttributeString
  notificationsEnabled?: boolean
  address: AddressDraft
  crossfitStartDate?: HidableAttributeString
  emergencyInformation?: string
  job?: HidableAttributeString
  weight?: HidableAttributeFloat
  height?: HidableAttributeFloat
  phoneNumber?: PhoneNumberWithPrefix
}

export function convertUserToDraft(user: UserProfile | null): UserProfileDraft {
  return {
    ...user,
    firstname: user?.firstname ?? "",
    lastname: user?.lastname ?? "",
    username: user?.username ?? "",
    avatar: user?.avatar
      ? {
          serverBlurHash: user.avatar.blurHash ?? "",
          serverPath: user.avatar.path ?? "",
          localPath: undefined,
          localBase64: undefined,
        }
      : undefined,
    notificationsEnabled: !!user?.notificationsEnabled,
    address: user?.address ? user.address : EmptyAddress,
  }
}

export const UserProfileEntity = {
  getSimpleProfileFields: (): (keyof UserProfile)[] => [
    "firstname",
    "lastname",
    "username",
    "avatar",
    "biography",
    "height",
    "weight",
    "emergencyInformation",
  ],
  getHideableFields: (): (keyof Pick<
    UserProfile,
    "nationality" | "job" | "birthdate" | "crossfitStartDate" | "genderCategory"
  >)[] => ["nationality", "job", "birthdate", "genderCategory", "crossfitStartDate"],
  getAddressFields: (): (keyof Address)[] => ["address", "city", "postalCode", "country"],
  getProfileCompletion: (profile: Partial<UserProfile>) => {
    const simpleProperties = UserProfileEntity.getSimpleProfileFields()

    const hideableProperties = UserProfileEntity.getHideableFields()

    const addressProperties = UserProfileEntity.getAddressFields()

    const totalProperties = simpleProperties.length + hideableProperties.length + addressProperties.length + 1 // phoneNumber
    let completedProperties = 0

    for (const prop of simpleProperties) {
      if (profile[prop]) {
        completedProperties++
      }
    }

    for (const prop of hideableProperties) {
      if (profile[prop]?.value) {
        completedProperties++
      }
    }

    for (const prop of addressProperties) {
      if (profile.address?.[prop]) {
        completedProperties++
      }
    }

    if (profile.phoneNumber?.number) {
      completedProperties++
    }

    const ratio = completedProperties / totalProperties

    const completion = {
      pcRatio: ratio,
      current: completedProperties,
      total: totalProperties,
    }

    return completion
  },
  isOnlyMissingAvatar: (profile: Partial<UserProfile>) => {
    const completion = UserProfileEntity.getProfileCompletion(profile)
    const isMissingAvatar = !!profile.avatar === false
    const isOnlyMissingOneProfileProperty = completion.current === completion.total - 1

    if (isOnlyMissingOneProfileProperty && isMissingAvatar) {
      return true
    }

    return false
  },
}
